import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY, Observable, of, } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import { catchError, map, switchMap, withLatestFrom, } from 'rxjs/operators';
import {
  AUTHZ_DISPLAY_ERROR,
  AUTHZ_EXTENSION_BEFORE_INIT_RESPONSE,
  AUTHZ_INIT,
  AUTHZ_INIT_PROCESS,
  AUTHZ_INIT_PROCESS_SUCCESS,
  AUTHZ_INIT_SUCCESS,
  AUTHZ_SELECT_STRUCTURE,
  AUTHZ_STRUCTURES_LIST,
  AUTHZ_STRUCTURES_LIST_SUCCESS,
  AuthZDisplayError,
  AuthZExtensionBeforeInit,
  AuthZExtensionBeforeInitResponse,
  AuthZInit,
  AuthZInitError,
  AuthZInitProcess,
  AuthZInitProcessSuccess,
  AuthZInitSuccess,
  AuthZSelectStructure,
  AuthZStructuresList,
  AuthZStructuresListSuccess
} from '../actions/authz.action';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AUTH_EXTENSION_BEFORE_AUTH_SUCCESS,
  AuthExtensionBeforeAuthSuccess,
  AuthExtensionBeforeAuthSuccessResponse,
  GoToUrl,
  SnackErrorComponent,
} from '@pcscol-portail/ng-auth';
import { AuthzConfig } from '../../conf/conf';
import { getStructureSelected } from '../selectors/authz.selectors';
import { AuthzNgRxAwareService } from '../../services/authz.service';

@Injectable()
export class AuthZEffects {
  private actions$ = inject(Actions);
  authNConnection$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTH_EXTENSION_BEFORE_AUTH_SUCCESS),
      map((action: AuthExtensionBeforeAuthSuccess) => new AuthZStructuresList())
    ));
  loadStructuresList$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_STRUCTURES_LIST),
      switchMap((action: AuthZStructuresList) => {
        return [
          new AuthZStructuresListSuccess([{code: 'ETAB00', libelleAffichage: 'Mon établissement Pégase'}]
          )];
      })
    ));
  selectStructure$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_STRUCTURES_LIST_SUCCESS),
      switchMap((action: AuthZStructuresListSuccess) => {
        if (action.autorizedStructuresList != null && action.autorizedStructuresList.length === 1) {
          return of(new AuthZSelectStructure(action.autorizedStructuresList[0]));
        } else {
          return of(new GoToUrl({url: this.config.select_etab_uri ?? this.config.missing_module_access_uri}));
        }
      }),
    ));


  // AuthN success interception effect
  changeStructure$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_SELECT_STRUCTURE),
      switchMap((action: AuthZSelectStructure) => this.hasAccessToCurrentModule().pipe(
        map((accessToCurrentModule) => {
            if (
              this.config.enableModuleAccessCheck
              && !accessToCurrentModule
            ) {
              return new GoToUrl({url: this.config.missing_module_access_uri});
            } else {
              return new AuthZInit(action.structure);
            }
          }
        )
      ))
    ));


  // Load autorized structures and filter them with REF
  // TODO - Code à simplifier
  authZInit$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_INIT),
      switchMap((action: AuthZInit) =>
        combineLatest(
          [
            this.authzService.lireListeModules(),
            this.authzService.lireListeTraitements(),
            this.authzService.lireListePerimetres(),
          ])
          .pipe(
            map(([modules, traitements, perimetres]: [string[], string[], string[]]) => new AuthZInitProcess(
              action.autorizedStructure,
              modules,
              traitements,
              perimetres
            )),
          )
      ),
      catchError(error => {
        return of(new AuthZInitError(error), new AuthZDisplayError(error));
      })
    ));

  // Generate autorized treatments list for the user and for the module requester
  authZInitProcess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_INIT_PROCESS),
      switchMap((action: AuthZInitProcess) => {
        return this.hasAccessToCurrentModule().pipe(
          map(access => new AuthZInitProcessSuccess(
              action.modules,
              // little glitch since a front app peut correspondre à n module
              this.config.code_module?.[0] ?? '',
              access,
              action.traitements,
              action.perimetres
            )
          ),
        );
      }),
      catchError(error => {
        return of(new AuthZInitError(error), new AuthZDisplayError(error));
      })
    ));
  authZInitBeforeInitSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_EXTENSION_BEFORE_INIT_RESPONSE),
      switchMap((action) => of(new AuthZInitSuccess())),
    ));

  // AuthZ initialisation effect
  authZInitSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_INIT_SUCCESS),
      switchMap((action) => {
        // on rend la main au module ng-auth pour finaliser l'authentification
        return of(new AuthExtensionBeforeAuthSuccessResponse());
      }),
    ));
  private snackBar = inject(MatSnackBar);
  displayError$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_DISPLAY_ERROR),
      switchMap((action: AuthZDisplayError) => {
        SnackErrorComponent.open(this.snackBar, action.payload);
        return EMPTY;
      })
    ), {dispatch: false});
  private store = inject<Store<any>>(Store);
  authZAfterInitProcessSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AUTHZ_INIT_PROCESS_SUCCESS),
      withLatestFrom(this.store.pipe(select(getStructureSelected))),
      map(([action, structure]) =>
        this.config.useExtensions && structure !== null ? new AuthZExtensionBeforeInit(structure)
          : new AuthZExtensionBeforeInitResponse()
      ),
    ));

  // Display error
  // TODO - Utiliser snackbar du theme
  private injector = inject(Injector);

  private get config(): AuthzConfig {
    return this.injector.get(AuthzConfig);
  }

  private get authzService(): AuthzNgRxAwareService {
    return this.injector.get(AuthzNgRxAwareService);
  }

  private hasAccessToCurrentModule(): Observable<boolean> {
    return this.authzService.hasAccessToAnyModule(this.config.code_module ?? []);
  }
}

